import React, { useState, useEffect, useContext } from 'react';
import { Avatar, Box } from '@mui/material';
import axios from '../../api/axios';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * UserAvatar - 显示用户头像的组件
 * 如果用户有头像，显示头像图片；否则显示用户名字的首字母
 * 支持头像框显示（根据用户当前选择的头像框）
 * 
 * @param {Object} user - 用户对象，可能包含 avatar、currentAvatar 或 authorAvatar 属性
 * @param {Object|string} avatar - 头像对象（包含 imageUrl）或头像URL字符串（可选，如果user中没有avatar信息）
 * @param {Object} frame - 头像框对象（可选，如果user中没有frame信息）
 * @param {Object} sx - Material-UI的sx样式属性
 * @param {string} size - 头像大小（可选，默认32）
 * @param {boolean} showFrame - 是否显示头像框（默认true）
 * @param {Object} ...other - 其他传递给Avatar组件的属性
 */
// 同步从 localStorage 读取头像框（仅当前用户），保证首屏就渲染边框
function getInitialFrameForCurrentUser(frame, user) {
  if (frame || user?.currentFrame) return frame || user?.currentFrame;
  if (typeof window === 'undefined') return null;
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      if (userData.currentFrame) return userData.currentFrame;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

function UserAvatar({ user, avatar, frame, sx = {}, size, showFrame = true, ...other }) {
  const { user: currentUser } = useContext(AuthContext);
  const isCurrentUser = Boolean(currentUser && user && user.id === currentUser.id);

  const getInitialFrame = () => {
    if (frame || user?.currentFrame) return frame || user?.currentFrame;
    if (!isCurrentUser) return null;
    return getInitialFrameForCurrentUser(frame, user);
  };

  const [currentFrame, setCurrentFrame] = useState(getInitialFrame);

  // 从 localStorage / user 再次同步（当 user/frame 从外部更新时）
  useEffect(() => {
    const next = frame || user?.currentFrame || (isCurrentUser ? getInitialFrameForCurrentUser(frame, user) : null);
    if (next && (!currentFrame || next.id !== currentFrame.id)) setCurrentFrame(next);
  }, [frame, user?.currentFrame, user, isCurrentUser, currentFrame]);

  // 仅当前用户：若没有 frame 信息则请求 /avatar-frames/current
  useEffect(() => {
    if (!isCurrentUser || !showFrame || currentFrame || frame) return;
    if (typeof window === 'undefined' || !user?.id) return;
    axios.get('/avatar-frames/current')
      .then(response => {
        if (response.data) setCurrentFrame(response.data);
      })
      .catch(err => {
        console.error('Error fetching current frame:', err);
      });
  }, [isCurrentUser, showFrame, currentFrame, frame, user?.id]);

  // 仅当前用户：监听 localStorage / 自定义事件（当用户选择新头像框时）
  useEffect(() => {
    if (typeof window === 'undefined' || !isCurrentUser) return;

    const handleStorageChange = () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          if (userData.currentFrame && userData.currentFrame.id !== currentFrame?.id) {
            setCurrentFrame(userData.currentFrame);
          }
        }
      } catch (e) {
        // Ignore errors
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userFrameUpdated', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userFrameUpdated', handleStorageChange);
    };
  }, [isCurrentUser, currentFrame]);

  // 优先使用传入的avatar，其次使用user.avatar、user.currentAvatar或user.authorAvatar
  const avatarData = avatar || user?.avatar || user?.currentAvatar;
  // 如果avatarData是字符串，直接使用；如果是对象，取imageUrl
  const imageUrl = typeof avatarData === 'string' 
    ? avatarData 
    : (avatarData?.imageUrl || user?.authorAvatar);
  const userName = user?.name || user?.authorName || '';
  const displayName = userName.charAt(0).toUpperCase() || 'U';

  // 20种霓虹色 - 手动定义，与 AvatarFrameCard 保持一致
  const neonColors = [
    { r: 255, g: 20, b: 147 },    // 0: 霓虹粉
    { r: 0, g: 255, b: 127 },     // 1: 霓虹绿
    { r: 0, g: 191, b: 255 },     // 2: 霓虹蓝
    { r: 255, g: 215, b: 0 },     // 3: 霓虹金
    { r: 255, g: 20, b: 20 },     // 4: 霓虹红
    { r: 138, g: 43, b: 226 },    // 5: 霓虹紫
    { r: 0, g: 255, b: 255 },     // 6: 霓虹青
    { r: 255, g: 105, b: 180 },   // 7: 霓虹粉红
    { r: 50, g: 205, b: 50 },     // 8: 霓虹青绿
    { r: 30, g: 144, b: 255 },    // 9: 霓虹天蓝
    { r: 255, g: 140, b: 0 },     // 10: 霓虹橙
    { r: 186, g: 85, b: 211 },    // 11: 霓虹紫罗兰
    { r: 64, g: 224, b: 208 },    // 12: 霓虹青绿
    { r: 255, g: 69, b: 0 },      // 13: 霓虹红橙
    { r: 147, g: 112, b: 219 },   // 14: 霓虹中紫
    { r: 0, g: 250, b: 154 },     // 15: 霓虹薄荷绿
    { r: 255, g: 0, b: 255 },     // 16: 霓虹洋红
    { r: 32, g: 178, b: 170 },    // 17: 霓虹浅海绿
    { r: 255, g: 165, b: 0 },     // 18: 霓虹橙
    { r: 148, g: 0, b: 211 }      // 19: 霓虹紫
  ];

  // Get neon color for frame based on badge count
  const getNeonColor = (badgeCount) => {
    const index = Math.min(badgeCount, 19);
    return neonColors[index];
  };

  // 获取头像框样式（使用与 AvatarFrameCard 相同的逻辑）
  const getFrameStyle = () => {
    if (!showFrame || !currentFrame) return {};
    
    const badgeCount = currentFrame.requiredBadges || 0;
    const color = getNeonColor(badgeCount);
    const rgbString = `rgb(${color.r}, ${color.g}, ${color.b})`;
    const rgbaString = (alpha) => `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    
    const borderWidth = 2 + Math.floor(badgeCount / 5);
    const shadowIntensity = 0.3 + (badgeCount * 0.025);
    const shadowSize = 4 + (badgeCount * 0.8);
    // 外发光：与头像框同色，更明显（大扩散 + 较高不透明度）
    const outerGlow = `0 0 28px ${rgbaString(0.75)}, 0 0 56px ${rgbaString(0.5)}, 0 0 84px ${rgbaString(0.28)}`;
    
    if (badgeCount < 5) {
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, ${outerGlow}`,
        backgroundColor: 'transparent'
      };
    } else if (badgeCount < 10) {
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, inset 0 0 ${shadowSize * 0.6}px ${rgbaString(0.1)}, ${outerGlow}`,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.1)}, ${rgbaString(0.15)})`,
        backgroundColor: 'transparent'
      };
    } else if (badgeCount < 15) {
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, 0 0 ${shadowSize * 1.5}px ${rgbaString(shadowIntensity * 0.5)}, inset 0 0 ${shadowSize * 0.7}px ${rgbaString(0.15)}, ${outerGlow}`,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.15)}, ${rgbaString(0.25)}, ${rgbaString(0.15)})`,
        backgroundColor: 'transparent',
        backgroundSize: '200% 200%',
        '@keyframes gradientShift': {
          '0%, 100%': {
            backgroundPosition: '0% 50%'
          },
          '50%': {
            backgroundPosition: '100% 50%'
          }
        },
        animation: 'gradientShift 3s ease infinite'
      };
    } else {
      const baseShadow = `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, 0 0 ${shadowSize * 2}px ${rgbaString(shadowIntensity * 0.6)}, 0 0 ${shadowSize * 3}px ${rgbaString(shadowIntensity * 0.3)}, inset 0 0 ${shadowSize}px ${rgbaString(0.2)}, ${outerGlow}`;
      const pulseShadow = `0 0 ${shadowSize * 1.3}px ${rgbaString(shadowIntensity * 1.2)}, 0 0 ${shadowSize * 2.5}px ${rgbaString(shadowIntensity * 0.8)}, 0 0 ${shadowSize * 4}px ${rgbaString(shadowIntensity * 0.5)}, ${outerGlow}`;
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: baseShadow,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.2)}, ${rgbaString(0.3)}, ${rgbaString(0.2)})`,
        backgroundColor: 'transparent',
        backgroundSize: '200% 200%',
        '@keyframes pulse': {
          '0%, 100%': { boxShadow: baseShadow },
          '50%': { boxShadow: pulseShadow }
        },
        '@keyframes gradientShift': {
          '0%, 100%': {
            backgroundPosition: '0% 50%'
          },
          '50%': {
            backgroundPosition: '100% 50%'
          }
        },
        animation: 'pulse 2s ease-in-out infinite, gradientShift 3s ease infinite'
      };
    }
  };

  const frameStyle = showFrame && currentFrame ? getFrameStyle() : {};

  // 合并样式（不包含头像框样式，头像框样式只应用到外层 Box）
  const avatarSx = {
    bgcolor: 'primary.main',
    fontSize: size ? `${size * 0.4}px` : '0.875rem',
    fontWeight: 600,
    ...sx
  };

  // 如果指定了size，设置宽高
  if (size) {
    avatarSx.width = size;
    avatarSx.height = size;
  }

  // 如果有头像框，使用Box包裹Avatar以应用边框效果
  if (showFrame && currentFrame) {
    // 计算合适的 padding（根据边框宽度）
    const borderWidth = frameStyle.border ? parseInt(frameStyle.border.match(/\d+/)?.[0]) || 3 : 3;
    const padding = Math.max(2, borderWidth);
    
    return (
      <Box
        sx={{
          display: 'inline-flex',
          borderRadius: '50%',
          padding: `${padding}px`,
          ...frameStyle,
          transition: 'all 0.3s',
          boxSizing: 'border-box',
          // 确保背景不会遮挡头像内容
          backgroundColor: frameStyle.background || 'transparent'
        }}
      >
        <Avatar
          src={imageUrl}
          alt={userName}
          sx={avatarSx}
          {...other}
        >
          {displayName}
        </Avatar>
      </Box>
    );
  }

  return (
    <Avatar
      src={imageUrl}
      alt={userName}
      sx={avatarSx}
      {...other}
    >
      {displayName}
    </Avatar>
  );
}

export default UserAvatar;
